import React, {useState} from 'react';
import {toast} from 'react-toastify';
import {z} from 'zod';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {DialogTitle} from '@headlessui/react';
import {CheckIcon} from '@heroicons/react/24/outline';
import {useRequestResetPassword, type RequestResetPasswordBody} from 'api/generated';
import {Button, Divider, InputText, Modal} from 'components';

const schema = z.object({
  email: z.string().email('Email is invalid'),
});

interface IModalResetPasswordProps {
  visible: boolean;
  onClose: () => void;
}

const ModalResetPassword = ({visible, onClose}: IModalResetPasswordProps) => {
  const [success, setSuccess] = useState(false);
  return (
    <Modal visible={visible} onClose={onClose}>
      {success ? <SuccessNotice onClose={onClose} /> : <RequestForm onRequested={() => setSuccess(true)} onClose={onClose} />}
    </Modal>
  );
};

export default React.memo(ModalResetPassword);

const RequestForm = ({onRequested, onClose}: {onRequested: () => void; onClose: () => void}) => {
  const requestResetPassword = useRequestResetPassword();
  const {
    register,
    watch,
    handleSubmit,
    formState: {errors, isDirty},
  } = useForm({
    resolver: zodResolver(schema),
  });
  const onSubmit = async (data: RequestResetPasswordBody) => {
    const {data: res} = await requestResetPassword.mutateAsync({data});
    if (res.data) {
      onRequested();
    } else if (res.errors) {
      toast.error(res.errors[0].detail);
    }
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full justify-between">
      <DialogTitle as="h3" className="text-base font-semibold text-gray-900 dark:text-white">
        Forgot Password
      </DialogTitle>
      <div className="mt-2 mb-2">
        <p className="text-sm text-gray-500 dark:text-darkText">Enter your email to request for password reset.</p>
      </div>
      <Divider />
      <InputText label="Email Address" type="email" autoComplete="email" {...register('email')} error={errors.email?.message} />
      <div className="mt-5 w-full flex items-center justify-between">
        <div className="w-20">
          <Button text="Cancel" fullWidth size="extra-small" variant="secondary" disabled={requestResetPassword.isPending} onClick={onClose} />
        </div>
        <div className="w-20">
          <Button
            type="submit"
            fullWidth
            text="Submit"
            size="extra-small"
            disabled={!watch('email') || !isDirty}
            loading={requestResetPassword.isPending}
          />
        </div>
      </div>
    </form>
  );
};

const SuccessNotice = ({onClose}: {onClose: () => void}) => {
  return (
    <>
      <div>
        <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-green-100">
          <CheckIcon aria-hidden="true" className="size-6 text-green-600" />
        </div>
        <div className="mt-3 text-center sm:mt-5">
          <DialogTitle as="h3" className="text-base font-semibold text-gray-900 dark:text-white">
            Password reset requested
          </DialogTitle>
          <div className="mt-2">
            <p className="text-sm text-gray-500 dark:text-darkText">Check your mailbox or maybe spam folder for the email.</p>
          </div>
        </div>
      </div>
      <div className="mt-5 sm:mt-6">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-xs hover:brightness-120 active:scale-95 duration-300 cursor-pointer">
          Close
        </button>
      </div>
    </>
  );
};
